import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Reminder } from '@/types/reminder';

interface RemindersContextType {
  reminders: Reminder[];
  dueReminders: Reminder[];
  upcomingReminders: Reminder[];
  isLoading: boolean;
  fetchReminders: () => Promise<void>;
  addReminder: (reminder: Omit<Reminder, 'id' | 'createdAt' | 'completed'>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
}

const RemindersContext = createContext<RemindersContextType | undefined>(undefined);

export function RemindersProvider({ children }: { children: ReactNode }) {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getApiUrl = () => {
    return import.meta.env.VITE_KV_API_URL || 'https://qandil-realtime-api.qandil.workers.dev';
  };

  const fetchReminders = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/reminders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReminders(data.filter((r: Reminder) => !r.completed));
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addReminder = async (reminder: Omit<Reminder, 'id' | 'createdAt' | 'completed'>) => {
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const newReminder: Reminder = {
        ...reminder,
        id: Date.now().toString(36) + Math.random().toString(36).substring(2),
        createdAt: new Date().toISOString(),
        completed: false,
      };

      const response = await fetch(`${getApiUrl()}/reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newReminder),
      });

      if (response.ok) {
        await fetchReminders();
      }
    } catch (error) {
      console.error('Error adding reminder:', error);
      throw error;
    }
  };

  const deleteReminder = async (id: string) => {
    try {
      const token = localStorage.getItem('auth-token') || import.meta.env.VITE_KV_TOKEN;
      const response = await fetch(`${getApiUrl()}/reminders/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchReminders();
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      throw error;
    }
  };

  // چێککردنی ئاگادارییە دواکەوتووەکان
  const dueReminders = reminders.filter(reminder => {
    const reminderDateTime = new Date(`${reminder.date}T${reminder.time}`);
    return reminderDateTime <= new Date();
  });

  // ئاگادارکردنەوەکانی داهاتوو
  const upcomingReminders = reminders.filter(reminder => {
    const reminderDateTime = new Date(`${reminder.date}T${reminder.time}`);
    return reminderDateTime > new Date();
  });

  // Fetch reminders on mount and every minute
  useEffect(() => {
    fetchReminders();
    const interval = setInterval(fetchReminders, 60000); // 1 خولەک
    return () => clearInterval(interval);
  }, []);

  return (
    <RemindersContext.Provider
      value={{
        reminders,
        dueReminders,
        upcomingReminders,
        isLoading,
        fetchReminders,
        addReminder,
        deleteReminder,
      }}
    >
      {children}
    </RemindersContext.Provider>
  );
}

export function useReminders() {
  const context = useContext(RemindersContext);
  if (context === undefined) {
    throw new Error('useReminders must be used within a RemindersProvider');
  }
  return context;
}
